import { useContextSelector } from 'use-context-selector';
import { DashboardContextValues } from '@app/types/core/dashboard';
import { DashboardContext } from '@shared/contexts/DashboardContext';

/**
 * @description Hook to select specific values from DashboardContext.
 * Only components using selected values will re-render when those values change.
 * @example
 * const isNotificationOpened = useDashboardSelector(state => state.isNotificationOpened);
 * const toggleNotificationOpened = useDashboardSelector(state => state.toggleNotificationOpened);
 */
export const useDashboardSelector = <T>(
  selector: (value: DashboardContextValues) => T
): T => {
  return useContextSelector(DashboardContext, (value) => {
    if (!value) {
      throw new Error('DashboardContext must be use inside DashboardProvider');
    }
    return selector(value);
  });
};

/**
 * @description Legacy hook for backward compatibility.
 * @deprecated Use useDashboardSelector instead for better performance.
 * This hook causes all components to re-render when any context value changes.
 */
const useDashboard = (): DashboardContextValues => {
  return useContextSelector(DashboardContext, (value) => {
    if (!value) {
      throw new Error('DashboardContext must be use inside DashboardProvider');
    }
    return value;
  });
};

export default useDashboard;
